/**
 * Hero Globe - COBE Integration (Orbe fork)
 * Initializes 3D globe in hero. Fill (Figma globe Radius 655:437) AND the
 * glass rim highlight both live in the assets/cobe.min.js fragment shader —
 * they track the sphere in any layout/distortion. City markers stay small.
 * glowColor is ink (not coral); coral lives only in the shader fill.
 */

class HeroGlobe {
  constructor(canvasElement) {
    this.canvas = canvasElement;
    this.globe = null;
    this.phi = 0;
    this.pointerInteracting = null;
    this.pointerInteractionMovement = 0;
    this.autoRotate = this.canvas.dataset.autoRotate === 'true';
    this.draggable = this.canvas.dataset.draggable === 'true';

    // City markers — keep small (0.02–0.03); previous 0.05–0.08 read as fat blobs
    this.markers = [
      { location: [40.7128, -74.0060], size: 0.025 },  // New York
      { location: [37.7595, -122.4367], size: 0.022 }, // San Francisco
      { location: [51.5074, -0.1278], size: 0.022 },   // London
      { location: [40.4168, -3.7038], size: 0.028 },   // Madrid
      { location: [-34.6037, -58.3816], size: 0.02 },  // Buenos Aires
      { location: [48.8566, 2.3522], size: 0.022 },    // Paris
      { location: [41.9028, 12.4964], size: 0.022 },   // Rome
      { location: [52.5200, 13.4050], size: 0.022 },   // Berlin
      { location: [59.3293, 18.0686], size: 0.02 },    // Stockholm
      { location: [55.6761, 12.5683], size: 0.02 },    // Copenhagen
      { location: [41.0082, 28.9784], size: 0.022 },   // Istanbul
      { location: [52.3676, 4.9041], size: 0.02 },     // Amsterdam
      { location: [-23.5505, -46.6333], size: 0.025 }, // São Paulo
      { location: [35.6762, 139.6503], size: 0.022 },  // Tokyo
      { location: [22.3193, 114.1694], size: 0.02 },   // Hong Kong
      { location: [19.0760, 72.8777], size: 0.022 },   // Mumbai
      { location: [-33.9249, 18.4241], size: 0.02 },   // Cape Town
      { location: [-33.8688, 151.2093], size: 0.022 }, // Sydney
      { location: [4.7110, -74.0721], size: 0.02 }     // Bogotá
    ];

    this.init();
  }
  
  init() {
    // Wait for COBE library if not loaded yet
    if (typeof createGlobe === 'undefined') {
      window.addEventListener('cobe-loaded', () => this.initGlobe(), { once: true });
      return;
    }
    
    // Ensure DOM is ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initGlobe());
    } else {
      requestAnimationFrame(() => this.initGlobe());
    }
  }
  
  initGlobe() {
    if (this.globe) return;
    
    this.createGlobeInstance();
    
    if (this.draggable && !this.interactionsSetup) {
      this.setupInteractions();
      this.interactionsSetup = true;
    }
    
    if (!this.resizeSetup) {
      this.setupResize();
      this.resizeSetup = true;
    }
  }
  
  createGlobeInstance() {
    // Destroy existing globe if present
    if (this.globe) {
      this.globe.destroy();
      this.globe = null;
    }
    
    // Set canvas resolution for crisp rendering
    const canvasSize = this.canvas.offsetWidth || 600;
    this.canvas.width = canvasSize * 2;
    this.canvas.height = canvasSize * 2;
    
    // Fill = Figma 655:437 in cobe.min.js. glowColor = ink rim (not coral).
    this.globe = createGlobe(this.canvas, {
      devicePixelRatio: 2,
      width: canvasSize * 2,
      height: canvasSize * 2,
      phi: 0,
      theta: 0.3,
      dark: 1,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 8,
      baseColor: [1, 1, 1],
      markerColor: [1, 1, 1],
      glowColor: [0.0471, 0.0157, 0.1294],
      markers: this.markers,
      scale: 1,
      offset: [0, 0],
      onRender: (state) => {
        if (!this.pointerInteracting && this.autoRotate) {
          this.phi += 0.002;
        }
        state.phi = this.phi + this.pointerInteractionMovement;
      }
    });
  }
  
  setupInteractions() {
    this.canvas.style.cursor = 'grab';
    
    const updateInteraction = (clientX) => {
      if (this.pointerInteracting !== null) {
        const delta = clientX - this.pointerInteracting;
        this.pointerInteractionMovement = delta * 0.01;
      }
    };
    
    // Mouse events
    this.canvas.addEventListener('mousedown', (e) => {
      this.pointerInteracting = e.clientX - this.pointerInteractionMovement;
      this.canvas.style.cursor = 'grabbing';
    });
    
    window.addEventListener('mouseup', () => {
      if (this.pointerInteracting !== null) {
        // Transfer the drag movement to phi and reset the interaction movement
        this.phi += this.pointerInteractionMovement;
        this.pointerInteractionMovement = 0;
      }
      this.pointerInteracting = null;
      this.canvas.style.cursor = 'grab';
    });
    
    window.addEventListener('mousemove', (e) => updateInteraction(e.clientX));
    
    // Touch events
    this.canvas.addEventListener('touchstart', (e) => {
      this.pointerInteracting = e.touches[0].clientX - this.pointerInteractionMovement;
    }, { passive: true });
    
    this.canvas.addEventListener('touchmove', (e) => {
      if (e.touches[0]) updateInteraction(e.touches[0].clientX);
    }, { passive: true });
    
    this.canvas.addEventListener('touchend', () => {
      if (this.pointerInteracting !== null) {
        // Transfer the drag movement to phi and reset the interaction movement
        this.phi += this.pointerInteractionMovement;
        this.pointerInteractionMovement = 0;
      }
      this.pointerInteracting = null;
    });
  }
  
  setupResize() {
    let resizeTimeout;
    let lastWidth = this.canvas.offsetWidth;
    let isRecreating = false; // Prevent multiple simultaneous recreations
    
    const handleResize = () => {
      // Skip if already recreating
      if (isRecreating) return;
      
      clearTimeout(resizeTimeout);
      
      // Longer debounce - users typically take 300-500ms to finish resizing
      resizeTimeout = setTimeout(() => {
        const currentWidth = this.canvas.offsetWidth;
        
        // Only recreate if size actually changed (threshold to avoid tiny changes)
        if (Math.abs(currentWidth - lastWidth) > 10) {
          isRecreating = true;
          lastWidth = currentWidth;
          
          // Use requestAnimationFrame for smoother recreation
          requestAnimationFrame(() => {
            this.createGlobeInstance();
            isRecreating = false;
          });
        }
      }, 300); // Increased from 150ms to 300ms
    };
    
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(handleResize);
      this.resizeObserver.observe(this.canvas.parentElement);
    } else {
      // Fallback to window resize for older browsers
      window.addEventListener('resize', handleResize);
    }
  }
  
  destroy() {
    if (this.globe) {
      this.globe.destroy();
      this.globe = null;
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

// Initialize all globe canvases
function initializeHeroGlobes() {
  document.querySelectorAll('.hero__globe-canvas').forEach(canvas => {
    new HeroGlobe(canvas);
  });
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHeroGlobes);
} else {
  initializeHeroGlobes();
}

// Export for manual use
if (typeof window !== 'undefined') {
  window.HeroGlobe = HeroGlobe;
}
